import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ForgeApiService } from '../../services/ForgeApiService';
import { VizSimDatasetClassSummary, VizSimDatasetSummary } from '../../types';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';
import {
  mergeVizSimClassTagsForAppend,
  normalizeTagPart,
  normalizeVizSimTags,
} from '../../utils/vizsimTags';
import { MasterLabelLink } from './MasterLabelLink';
import { VizSimTagEditor } from './VizSimTagEditor';

type NotificationType = 'success' | 'warning' | 'error';

interface VizSimDatasetAddDialogProps {
  token: string;
  chainId: number;
  selectedCropIds: string[];
  classNameSuggestion?: string | null;
  onClose: () => void;
  onNotify: (message: string, type?: NotificationType) => void;
  onAdded?: (dataset: VizSimDatasetSummary) => void;
}

function simpleHash(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(16).padStart(8, '0').slice(0, 12);
}

export const VizSimDatasetAddDialog: React.FC<VizSimDatasetAddDialogProps> = ({
  token,
  chainId,
  selectedCropIds,
  classNameSuggestion,
  onClose,
  onNotify,
  onAdded,
}) => {
  const [classNameInput, setClassNameInput] = useState(classNameSuggestion ?? '');
  const [classHashInput, setClassHashInput] = useState('');
  const [sampleTags, setSampleTags] = useState<string[]>([]);
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [existingClasses, setExistingClasses] = useState<VizSimDatasetClassSummary[]>([]);
  const [modPrefixes, setModPrefixes] = useState<string[]>([]);
  const [selectedCropStoreIds, setSelectedCropStoreIds] = useState<number[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didEditTagsRef = useRef(false);
  const didEditClassNameRef = useRef(false);
  const didEditNotesRef = useRef(false);
  const lastAppliedExistingClassRef = useRef<string | null>(null);

  const selectedCropKey = useMemo(() => [...selectedCropIds].sort().join('|'), [selectedCropIds]);
  const selectedCropRequestIds = useMemo(
    () => (selectedCropKey ? selectedCropKey.split('|') : []),
    [selectedCropKey]
  );
  const normalizedClassHashInput = normalizeTagPart(classHashInput);
  const generatedClassHash = simpleHash(`${chainId}:${selectedCropKey}`);
  const effectiveClassHash = normalizedClassHashInput || generatedClassHash;
  const reusableExistingTags = useMemo(() => normalizeVizSimTags(existingTags), [existingTags]);
  const matchedExistingClass = useMemo(
    () => existingClasses.find((classItem) => classItem.classHash === normalizedClassHashInput),
    [existingClasses, normalizedClassHashInput]
  );
  const selectedExistingClass = useMemo(
    () => existingClasses.find((classItem) => classItem.classHash === effectiveClassHash),
    [effectiveClassHash, existingClasses]
  );
  const shouldWarnAboutAdditionalClass = existingClasses.length > 0 && !selectedExistingClass;
  const isUsingGeneratedClassHash = !normalizedClassHashInput;
  const generatedClassNeedsMoreCrops = isUsingGeneratedClassHash && selectedCropIds.length < 2;
  const canSubmit =
    selectedCropIds.length > 0 &&
    !generatedClassNeedsMoreCrops &&
    !isLoadingSuggestions &&
    sampleTags.length > 0;

  useEffect(() => {
    let cancelled = false;
    didEditTagsRef.current = false;
    didEditClassNameRef.current = false;
    didEditNotesRef.current = false;
    lastAppliedExistingClassRef.current = null;
    setIsLoadingSuggestions(true);
    setModPrefixes([]);
    setExistingTags([]);
    setExistingClasses([]);
    setSelectedCropStoreIds([]);
    setClassHashInput('');
    setSampleTags([]);
    setClassNameInput(classNameSuggestion ?? '');

    ForgeApiService.fetchVizSimDatasetTagSuggestions(token, { crop_ids: selectedCropRequestIds })
      .then((response) => {
        if (cancelled) return;
        setModPrefixes(response.modPrefixes);
        setExistingTags(response.existingTags);
        setExistingClasses(response.existingClasses ?? []);
        setSelectedCropStoreIds(response.storeIds ?? []);
        if (!didEditTagsRef.current) {
          setSampleTags(normalizeVizSimTags(response.tags));
        }
        if (!didEditClassNameRef.current) {
          setClassNameInput(classNameSuggestion || response.className || '');
        }
      })
      .catch((error) => {
        if (cancelled) return;
        onNotify(
          error instanceof Error ? error.message : 'Failed to load VizSim tag suggestions',
          'warning'
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSuggestions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [classNameSuggestion, onNotify, selectedCropKey, selectedCropRequestIds, token]);

  useEffect(() => {
    if (!matchedExistingClass) return;
    if (lastAppliedExistingClassRef.current === matchedExistingClass.classHash) return;
    lastAppliedExistingClassRef.current = matchedExistingClass.classHash;
    if (!didEditClassNameRef.current) {
      setClassNameInput(matchedExistingClass.className ?? '');
    }
    if (!didEditTagsRef.current) {
      setSampleTags(
        mergeVizSimClassTagsForAppend(
          matchedExistingClass.tags,
          sampleTags,
          matchedExistingClass.storeIds ?? [],
          selectedCropStoreIds
        )
      );
    }
    if (!didEditNotesRef.current) {
      setNotes(matchedExistingClass.notes ?? '');
    }
  }, [matchedExistingClass, sampleTags, selectedCropStoreIds]);

  const updateTagsInput = (nextTags: string[]) => {
    didEditTagsRef.current = true;
    setSampleTags(normalizeVizSimTags(nextTags));
  };

  const applyExistingClass = (classItem: VizSimDatasetClassSummary) => {
    lastAppliedExistingClassRef.current = classItem.classHash;
    setClassHashInput(classItem.classHash);
    setClassNameInput(classItem.className ?? '');
    setSampleTags(
      mergeVizSimClassTagsForAppend(
        classItem.tags,
        sampleTags,
        classItem.storeIds ?? [],
        selectedCropStoreIds
      )
    );
    setNotes(classItem.notes ?? '');
    didEditClassNameRef.current = false;
    didEditTagsRef.current = false;
    didEditNotesRef.current = false;
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const response = await ForgeApiService.addVizSimDatasetChainCrops(token, chainId, {
        crop_ids: selectedCropIds,
        label: null,
        class_hash: normalizeTagPart(effectiveClassHash),
        class_name: classNameInput.trim() || null,
        tags: sampleTags,
        notes: notes.trim() || null,
      });
      const changedCount = response.addedCount + response.updatedCount;
      onNotify(
        `Added ${changedCount} crop${changedCount !== 1 ? 's' : ''} to chain ${response.dataset.chainId} VizSim dataset`,
        'success'
      );
      onAdded?.(response.dataset);
      onClose();
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : 'Failed to add crops to VizSim dataset',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vizsim-modal-backdrop" role="presentation">
      <div
        className="vizsim-dialog vizsim-add-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Add selected crops to VizSim dataset"
      >
        <div className="vizsim-dialog-header">
          <div>
            <div className="dg-panel-title">
              <DatasetOutlinedIcon />
              VizSim Dataset
            </div>
            <div className="vizsim-muted">
              {selectedCropIds.length} selected crops • chain {chainId}
            </div>
          </div>
          <button type="button" className="dg-secondary-button" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="vizsim-edit-grid">
          <label className="vizsim-field">
            <span>Class hash</span>
            <input
              value={classHashInput}
              placeholder={generatedClassHash}
              onChange={(event) => {
                lastAppliedExistingClassRef.current = null;
                setClassHashInput(event.target.value);
              }}
            />
          </label>
          <label className="vizsim-field">
            <span>Class name</span>
            <input
              value={classNameInput}
              placeholder="Optional display name"
              onChange={(event) => {
                didEditClassNameRef.current = true;
                setClassNameInput(event.target.value);
              }}
            />
          </label>
        </div>
        {!normalizedClassHashInput ? (
          <div className="vizsim-tag-hint">New class hash {generatedClassHash}</div>
        ) : null}
        {generatedClassNeedsMoreCrops ? (
          <div className="vizsim-tag-hint">
            Select at least 2 crops to create a new class, or paste an existing class hash.
          </div>
        ) : null}
        {existingClasses.length ? (
          <div
            className={`vizsim-class-options ${
              shouldWarnAboutAdditionalClass ? 'has-warning' : ''
            }`}
          >
            {shouldWarnAboutAdditionalClass ? (
              <div className="vizsim-class-options-warning" role="note">
                <strong>This item number already has VizSim classes.</strong>
                <span>
                  Creating another class will split this item number. Append to an existing class
                  unless this is intentional.
                </span>
              </div>
            ) : null}
            <div className="vizsim-class-options-header">
              <span>Related classes</span>
              <span className="system-health-muted">Open in Master Label or append here</span>
            </div>
            <div className="vizsim-class-options-list">
              {existingClasses.map((classItem) => {
                const isActive = classItem.classHash === effectiveClassHash;
                const classLabel = classItem.className || classItem.classHash;
                const cropIds = classItem.cropIds ?? [];
                return (
                  <div
                    key={classItem.classHash}
                    className={`vizsim-class-option ${isActive ? 'is-active' : ''}`}
                  >
                    {cropIds.length ? (
                      <MasterLabelLink
                        to={buildMasterLabelUrl({
                          chainId,
                          cropIds,
                          highlightMode: 'similar',
                        })}
                        variant="forge"
                        className="vizsim-class-option-label"
                        aria-label={classLabel}
                      >
                        <strong>{classItem.classHash}</strong>
                        {classItem.className ? <span>{classItem.className}</span> : null}
                      </MasterLabelLink>
                    ) : (
                      <span className="vizsim-class-option-label">
                        <strong>{classItem.classHash}</strong>
                        {classItem.className ? <span>{classItem.className}</span> : null}
                      </span>
                    )}
                    <em>{classItem.cropCount} crops</em>
                    <button
                      type="button"
                      className="vizsim-class-option-action"
                      aria-pressed={isActive}
                      aria-label={`Use existing class ${classItem.classHash}`}
                      onClick={() => applyExistingClass(classItem)}
                    >
                      {isActive ? 'Selected' : 'Append'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <VizSimTagEditor
          tags={sampleTags}
          existingTags={reusableExistingTags}
          modPrefixes={modPrefixes}
          onChange={updateTagsInput}
        />
        {isLoadingSuggestions ? (
          <div className="vizsim-tag-hint">Loading suggested tags...</div>
        ) : null}
        <label className="vizsim-field">
          <span>Notes</span>
          <textarea
            value={notes}
            rows={2}
            onChange={(event) => {
              didEditNotesRef.current = true;
              setNotes(event.target.value);
            }}
          />
        </label>

        <div className="vizsim-dialog-actions">
          <button
            type="button"
            className="dg-primary-button"
            disabled={!canSubmit || isSubmitting}
            onClick={handleSubmit}
          >
            <AddOutlinedIcon />
            Add Crops
          </button>
        </div>
      </div>
    </div>
  );
};
